/**
 * GroupParamGate (#1710)
 *
 * Consumes the `?group=` deep-link parameter once, at route level, for every
 * destination a summary email links to (snapshots, payday funding, budget and
 * ledger detail). Without it each page would render whatever workspace the
 * browser happens to have active, not the workspace the email came from.
 *
 * The gate is derived from the URL, not from effect state: while `?group=` is
 * present the routed page does not render at all. That ordering is load-bearing
 * because the client caches are global rather than group-keyed (see the comment
 * atop GroupContext.jsx), so a page mounted against the previous workspace would
 * paint the wrong balances before correcting itself.
 *
 * A group the user does not belong to, a guide-sandbox group, or an unknown id
 * degrades silently: no switch, parameter stripped, plain page in the current
 * workspace. `?schedule=` is deliberately left in the URL - Snapshots owns it.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useGroup } from '../contexts/GroupContext.jsx';
import { CenteredLoader } from './LoadingIndicator';

const GroupParamGate = ({ children }) => {
  const [searchParams] = useSearchParams();
  const groupParam = searchParams.get('group');
  const { groups, activeGroupId, loading: groupsLoading, setActiveGroupId } = useGroup();
  const location = useLocation();
  const navigate = useNavigate();
  // Tracks the value already handled. Required, not defensive: setActiveGroupId
  // is rebuilt whenever location.pathname changes, so this effect re-runs after
  // the strip navigation lands.
  const consumedRef = useRef(null);

  useEffect(() => {
    if (!groupParam) return;
    // Hold while the workspace list is in flight - setActiveGroupId no-ops on an
    // empty list, so consuming now would drop the parameter on the floor. The
    // effect re-runs when loadGroups resolves.
    if (groupsLoading) return;
    if (consumedRef.current === groupParam) return;
    consumedRef.current = groupParam;

    const target = (groups || []).find((g) => g.groupId === groupParam && !g.guideSandbox);
    if (target && target.groupId !== activeGroupId) {
      setActiveGroupId(groupParam, { keepPath: true });
    }

    const next = new URLSearchParams(location.search);
    next.delete('group');
    const search = next.toString();
    // replace, so the parameter cannot come back via Back or a refresh.
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [
    groupParam,
    groupsLoading,
    groups,
    activeGroupId,
    setActiveGroupId,
    navigate,
    location.pathname,
    location.search
  ]);

  if (groupParam) return <CenteredLoader />;
  return children;
};

export default GroupParamGate;
